import React, { useMemo } from "react";
import { MathJax } from "better-react-mathjax";

export const mathJaxConfig = {
  startup: {
    typeset: false,
  },
  loader: {
    load: [
      "input/tex",
      "output/svg",
      "[tex]/textmacros",
      "[tex]/textcomp",
      "[tex]/mhchem",
      "[tex]/physics",
      "[tex]/mathtools",
      "[tex]/cancel",
      "[tex]/bbox",
      "[tex]/bussproofs",
      "[tex]/cases",
      "[tex]/empheq",
      "[tex]/extpfeil",
    ],
  },
  options: {
    enableMenu: false,
    enableExplorer: false,
    enableAssistiveMml: false,
    skipHtmlTags: ["script", "noscript", "style", "textarea", "pre", "code"],
    ignoreHtmlClass: "tex2jax_ignore",
  },
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
    processEscapes: true,
    processEnvironments: true,
    packages: {
      "[+]": [
        "textmacros",
        "textcomp",
        "mhchem",
        "physics",
        "mathtools",
        "cancel", 
        "bbox",
        "bussproofs",
        "cases",
        "empheq",
        "extpfeil",
      ],
    },
  },
  svg: {
    fontCache: "none",
  },
  mml: {
    verify: {
      checkArity: false,
      checkAttributes: false,
      checkMathvariants: false,
      fullErrors: false,
    },
  },
};

export const mathJaxSrc =
  "https://cdn.jsdelivr.net/npm/mathjax@4.1.3/tex-mml-svg.js";

/**
 * Universal Preprocessor:
 * 1. Fixes string-escaped newlines (\n).
 * 2. Un-escapes double-escaped backslashes (\\ -> \).
 * 3. Detects <math> tags containing TeX macros/delimiters and extracts them
 *    into regular TeX spans.
 * 4. Leaves pure MathML (<math><msubsup>...</math>) untouched - MathJax's own
 *    MathML input jax renders those now (see mathJaxSrc above).
 *
 * NOTE: nothing imports this yet. The PDF preview pipeline
 * (build-test/routes/preview -> mathjax-editor) still runs its content through
 * processMathContent() in src/utils/helpers.ts.
 */
export function sanitizeMathHtml(rawHtml: string): string {
  if (!rawHtml || typeof rawHtml !== "string") return "";

  // Normalize string-escaped newlines
  const unescapedHtml = rawHtml.replace(/\\n/g, "\n");

  const parser = new DOMParser();
  const doc = parser.parseFromString(unescapedHtml, "text/html");

  const mathNodes = doc.querySelectorAll("math");
  mathNodes.forEach((mathNode) => {
    const content = mathNode.textContent || "";

    const hasTeXDelimiters =
      content.includes("$") ||
      content.includes("\\[") ||
      content.includes("\\(");
    const hasTeXCommands = /\\[a-zA-Z]+/.test(content);

    if (!hasTeXDelimiters && !hasTeXCommands) return;

    // Fix double backslashes inside TeX blocks
    let cleanedTex = content.replace(/\\\\/g, "\\").trim();

    // Drop any authored "$" wrapper so it can be re-delimited below.
    cleanedTex = cleanedTex.replace(/^\$\$?([\s\S]*?)\$\$?$/, "$1").trim();

    if (
      !cleanedTex.startsWith("\\[") &&
      !cleanedTex.startsWith("\\(")
    ) {
      cleanedTex = `\\(${cleanedTex}\\)`;
    }

    // Replace broken <math> tag with a clean TeX span
    const span = doc.createElement("span");
    span.className = "tex2jax_process";
    span.textContent = cleanedTex;
    mathNode.parentNode?.replaceChild(span, mathNode);
  });

  return doc.body.innerHTML;
}

interface UniversalMathRendererProps {
  htmlContent: string;
}

const UniversalMathRenderer: React.FC<UniversalMathRendererProps> = ({
  htmlContent,
}) => {
  // Preprocess HTML whenever input content changes
  const processedHtml = useMemo(
    () => sanitizeMathHtml(htmlContent),
    [htmlContent]
  );

  return (
    <MathJax dynamic inline hideUntilTypeset="first">
      <div
        className="mathjax-wrapper tex2jax_process"
        dangerouslySetInnerHTML={{ __html: processedHtml }}
      />
    </MathJax>
  );
};

export default UniversalMathRenderer;
